import React from 'react'
import PropTypes from 'prop-types'
import { parseLocalDate, formatTime } from '../../utils/dateHelpers'

/**
 * AnnouncementCard Component
 *
 * Displays league announcements with different styling for different announcement types
 *
 * @param {Object} announcement - Announcement object
 * @param {boolean} showActions - Show action buttons
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {Function} onRespond - Callback when respond button is clicked (for sub requests)
 * @param {string} type - Announcement type override (general, sub_request)
 * @param {string} className - Additional CSS classes
 */
const AnnouncementCard = React.memo(({
  announcement,
  showActions = false,
  onDelete = null,
  onRespond = null,
  type = null,
  className = ''
}) => {
  if (!announcement) {
    return null
  }

  const announcementType = type || announcement.announcement_type
  const isSubRequest = announcementType === 'sub_request'

  return (
    <div
      className={`border rounded-lg p-4 ${
        isSubRequest
          ? 'bg-amber-50 border-amber-200'
          : 'bg-slate-50 border-slate-200'
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 mt-0.5 ${
          isSubRequest ? 'text-amber-600' : 'text-slate-500'
        }`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h5 className="font-semibold text-slate-900 text-sm">{announcement.title}</h5>
              {isSubRequest && announcement.game_date && (
                <div className="text-xs text-slate-600 mt-1">
                  {parseLocalDate(announcement.game_date).toLocaleDateString()} at {formatTime(announcement.game_time)}
                  {announcement.rink_name && ` - ${announcement.rink_name}`}
                </div>
              )}
            </div>
            <div className="flex items-start gap-2">
              <span className="text-xs text-slate-500">
                {new Date(announcement.created_at).toLocaleDateString()}
              </span>
              {showActions && onDelete && isSubRequest && (
                <button
                  onClick={onDelete}
                  className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                  title="Delete sub request"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <p className="text-slate-700 text-sm whitespace-pre-wrap">{announcement.message}</p>
          {announcement.expires_at && (
            <p className="text-xs text-slate-500 mt-2">
              Expires: {new Date(announcement.expires_at).toLocaleDateString()}
            </p>
          )}
          {/* Sub Availability Button */}
          {isSubRequest && onRespond && (
            <div className="mt-3 pt-3 border-t border-amber-200">
              <button
                onClick={onRespond}
                className="btn-warning btn-sm"
              >
                I Can Sub for This Game
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

AnnouncementCard.displayName = 'AnnouncementCard'

AnnouncementCard.propTypes = {
  announcement: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    created_at: PropTypes.string,
    expires_at: PropTypes.string,
    announcement_type: PropTypes.string,
    game_date: PropTypes.string,
    game_time: PropTypes.string,
    rink_name: PropTypes.string,
  }),
  showActions: PropTypes.bool,
  onDelete: PropTypes.func,
  onRespond: PropTypes.func,
  type: PropTypes.string,
  className: PropTypes.string,
}

export default AnnouncementCard
